import type { ProviderFilter } from './services/invoices.service';
import { isValidReferenceMonth } from './components/invoice-formatters';

export const PROVIDER_FILTERS: ProviderFilter[] = ['all', 'submitted', 'missing'];

export interface InvoiceMonthSearch {
  referenceMonth?: string;
}

export interface InvoiceListSearch extends InvoiceMonthSearch {
  filter?: ProviderFilter;
  page?: number;
  search?: string;
}

export function parseReferenceMonth(value: unknown): string | undefined {
  return isValidReferenceMonth(value) ? value : undefined;
}

export function parsePage(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(n) && n > 1 ? n : undefined;
}

export function parseSearchText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function parseFilter(value: unknown): ProviderFilter | undefined {
  return PROVIDER_FILTERS.includes(value as ProviderFilter) && value !== 'all'
    ? (value as ProviderFilter)
    : undefined;
}

export function validateMonthSearch(search: Record<string, unknown>): InvoiceMonthSearch {
  return { referenceMonth: parseReferenceMonth(search.referenceMonth) };
}

export function validateListSearch(search: Record<string, unknown>): InvoiceListSearch {
  return {
    referenceMonth: parseReferenceMonth(search.referenceMonth),
    filter: parseFilter(search.filter),
    page: parsePage(search.page),
    search: parseSearchText(search.search),
  };
}

export type HistoryOrigin = 'list' | 'dashboard' | 'profile';

/** Search params do histórico: paginação própria + contexto da tela de origem (para "Voltar"). */
export interface InvoiceHistorySearch {
  page?: number;
  from?: HistoryOrigin;
  referenceMonth?: string;
  filter?: ProviderFilter;
  listPage?: number;
  listSearch?: string;
  /** Prestador de origem quando `from=profile`. */
  profileUserId?: string;
}

export function validateHistorySearch(search: Record<string, unknown>): InvoiceHistorySearch {
  const from =
    search.from === 'list' || search.from === 'dashboard' || search.from === 'profile'
      ? (search.from as HistoryOrigin)
      : undefined;
  const profileUserId =
    from === 'profile' && typeof search.profileUserId === 'string' && search.profileUserId
      ? search.profileUserId
      : undefined;
  return {
    page: parsePage(search.page),
    from,
    referenceMonth:
      from === 'list' || from === 'dashboard'
        ? parseReferenceMonth(search.referenceMonth)
        : undefined,
    filter: from === 'list' ? parseFilter(search.filter) : undefined,
    listPage: from === 'list' ? parsePage(search.listPage) : undefined,
    listSearch: from === 'list' ? parseSearchText(search.listSearch) : undefined,
    profileUserId,
  };
}

/** Destino do botão "Voltar" restaurando os filtros da origem. */
export function resolveHistoryBack(search: InvoiceHistorySearch):
  | { to: '/admin/invoices'; search: InvoiceMonthSearch }
  | { to: '/admin/invoices/submitted'; search: InvoiceListSearch }
  | { to: '/employees/$id'; params: { id: string } } {
  if (search.from === 'profile' && search.profileUserId) {
    return { to: '/employees/$id', params: { id: search.profileUserId } };
  }
  if (search.from === 'dashboard') {
    return { to: '/admin/invoices', search: { referenceMonth: search.referenceMonth } };
  }
  return {
    to: '/admin/invoices/submitted',
    search: {
      referenceMonth: search.referenceMonth,
      filter: search.filter,
      page: search.listPage,
      search: search.listSearch,
    },
  };
}
